import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MemberRole } from '@app/core/interfaces/c5/enumerations';
import { Role } from '@app/core/interfaces/c5/role';
import { SecurityPermissionName } from '@app/core/models/permission';
import { ResetMeetingPinOrTokenType } from '@app/core/repositories/meeting.repository';
import { FilterInputComponent } from '@app/shared/components/filter-input/filter-input.component';
import { PageChangeEvent } from '@app/shared/components/pagination/page-change-event';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { FilterableEntry } from '@app/shared/models/filterable-entry';
import { ContactNamePipe } from '@app/shared/pipes/contact-name.pipe';
import { PhoneNumberPipe } from '@app/shared/pipes/phone-number.pipe';
import { PortalDatePipe } from '@app/shared/pipes/portal-date.pipe';
import { FadeInOut } from '@app/utils/angular-animations';
import { Cached } from '@app/utils/cached';
import { PortalError } from '@app/utils/error/portal-error';
import { MetaDataProperty, multiOrProperty } from '@app/utils/lists/meta-list';
import { PagedList } from '@app/utils/lists/paged-list/paged-list';
import { PagedListImpl } from '@app/utils/lists/paged-list/paged-list-impl';
import { ResetPinOrTokenComponent } from '@business/meeting/components/reset-pin-or-token/reset-pin-or-token.component';
import { UpdateMeetingComponent } from '@business/meeting/components/update-meeting/update-meeting.component';
import { MeetingService } from '@business/meeting/meeting.service';
import { MeetingViewModel } from '@business/meeting/models/meeting-view-model';
import { MeetingsViewModel } from '@business/meeting/models/meetings-view-model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { setSeconds } from 'date-fns';
import { Observable, Subject } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-meeting-page',
  templateUrl: './meeting-page.component.html',
  styles: [],
  animations: [FadeInOut],
  standalone: false,
})
export class MeetingPageComponent implements OnInit {
  @ViewChild('filter', { read: FilterInputComponent })
  filter: FilterInputComponent<MeetingViewModel>;

  hasMeetings$!: Observable<boolean>;
  isMultiSite$!: Observable<boolean>;
  meetings$!: Observable<MeetingViewModel[]>;

  private viewModel$ = new Subject<MeetingsViewModel>();

  MemberRole = MemberRole;
  Permission = SecurityPermissionName;
  Role = Role;

  error: PortalError | null = null;
  list!: PagedList<MeetingViewModel>;
  metaDataProperties!: MetaDataProperty[];
  resetId: number | null = null;
  viewModel: Cached<MeetingsViewModel>;

  constructor(
    private clipboard: Clipboard,
    private contactNamePipe: ContactNamePipe,
    private datePipe: PortalDatePipe,
    private dialogService: DialogService,
    private meetingService: MeetingService,
    private phoneNumberPipe: PhoneNumberPipe,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.list = new PagedListImpl({
      entries: [],
      page: 1,
      pageSize: 12,
    });

    this.metaDataProperties = [];
    this.viewModel = this.meetingService.meetings;

    this.hasMeetings$ = this.viewModel.data$.pipe(
      map((viewModel) => viewModel.meetings.length > 0),
      untilDestroyed(this)
    );

    this.isMultiSite$ = this.viewModel.data$.pipe(
      map((viewModel) => viewModel.sites.length > 1),
      untilDestroyed(this)
    );

    this.meetings$ = this.viewModel.data$.pipe(
      map((viewModel) => viewModel.meetings),
      untilDestroyed(this)
    );

    this.viewModel.data$.pipe(untilDestroyed(this)).subscribe((viewModel) => {
      this.viewModel$.next(viewModel);

      this.list.updateEntries(viewModel.meetings).draw();

      this.metaDataProperties = [
        multiOrProperty('hasDedicatedLink', 'meeting.filter.hasDedicatedLink', [
          'boolean.yesorno.true',
          'boolean.yesorno.false',
        ]),
        multiOrProperty('isScheduled', 'meeting.scheduled', [
          'boolean.yesorno.true',
          'boolean.yesorno.false',
        ]),
        multiOrProperty('hasOwner', 'meeting.filter.hasOwner', [
          'boolean.yesorno.true',
          'boolean.yesorno.false',
        ]),
      ];

      if (viewModel.sites.length > 1) {
        this.metaDataProperties.unshift(
          multiOrProperty(
            'site',
            'site',
            viewModel.sites.map((site) => site.name)
          )
        );
      }
    });
  }

  add(): void {
    const activeSites: string[] = this.filter?.metaList.getActiveProperties()['site'] ?? [];
    let siteId: number | null = null;

    if (activeSites.length === 1) {
      siteId = this.viewModel.snapshot.sites.find((site) => site.name === activeSites[0])?.id;
    }

    this.dialogService.open(UpdateMeetingComponent).withInput({
      defaultSiteId: this.viewModel.snapshot.defaultSiteId,
      endUsers: this.viewModel.snapshot.endUsers,
      siteId,
      sites: this.viewModel.snapshot.sites,
    });
  }

  calculateMetaData = (meeting: MeetingViewModel): FilterableEntry => {
    let key: string = meeting.name;

    if (meeting.owner != null) {
      key += `:${this.contactNamePipe.transform(meeting.owner, { type: 'long' })}`;
    }

    return {
      key,
      meta: {
        hasDedicatedLink: meeting.permanentUrl != null ? 'boolean.yesorno.true' : 'boolean.yesorno.false',
        hasOwner: `boolean.yesorno.${meeting.owner != null ? 'true' : 'false'}`,
        isScheduled: meeting.start != null ? 'boolean.yesorno.true' : 'boolean.yesorno.false',
        site: meeting.site,
      },
    };
  };

  copyInvitation(meeting: MeetingViewModel): void {
    let dialIn: string = '';
    const link = [meeting.userUrl];

    if (meeting.userPassphrase) {
      link.push(
        this.translateService.instant('meeting.invitation.passphrase', {
          link: meeting.userUrl,
          passphrase: meeting.userPassphrase,
        })
      );
    }

    if (meeting.dialIn) {
      dialIn = this.translateService.instant('meeting.invitation.dialInTemplate', {
        phoneNumber: this.phoneNumberPipe.transform(meeting.phoneNumber, 'clean'),
        pin: meeting.userPin,
      });
    }

    this.clipboard.copy(
      this.translateService.instant('meeting.invitation.template', {
        dialIn: dialIn,
        link: link.join('\t'),
        name: meeting.name,
        participants:
          meeting.members
            .map((member) =>
              this.contactNamePipe.transform(member, {
                type: 'long',
              })
            )
            .join(', ') || this.translateService.instant('meeting.invitation.noMembers'),
        start:
          meeting.start != null
            ? this.datePipe.transform(setSeconds(meeting.start, 0), 'long')
            : this.translateService.instant('meeting.permanent'),
      })
    );
  }

  delete(meeting: MeetingViewModel): void {
    this.dialogService.openRemovalConfirmationDialog(() => this.meetingService.delete(meeting.id));
  }

  edit(meeting: MeetingViewModel): void {
    this.dialogService.open(UpdateMeetingComponent).withInput({
      defaultSiteId: this.viewModel.snapshot.defaultSiteId,
      endUsers: this.viewModel.snapshot.endUsers,
      meeting,
      sites: this.viewModel.snapshot.sites,
    });
  }

  filterChanged(meetings: MeetingViewModel[]): void {
    this.list.updateFilteredEntries(meetings).draw();
  }

  openResetDialog(id: number): void {
    this.dialogService.open(ResetPinOrTokenComponent).withInput({
      meetingId: id,
    });
  }

  pageChanged(event: PageChangeEvent): void {
    this.list.goTo(event.page).updatePageSize(event.pageSize).draw();
  }

  refresh(): void {
    this.viewModel.reload();
  }

  resetPinOrToken(id: number, type: ResetMeetingPinOrTokenType): void {
    this.resetId = id;

    this.meetingService
      .resetPinOrToken(id, type)
      .pipe(
        finalize(() => {
          this.resetId = null;
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
